import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../auth/AuthProvider";
import { useI18n } from "../../i18n";
import { shouldAutoFocusEditable } from "../../lib/mobileFocus";

/** Client mirror of the backend slugify (lowercase, alphanumeric + dashes). */
function slugify(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+/, "")
    .slice(0, 40);
}

/**
 * "Create workspace" modal — a name + a URL slug, in our dark theme. The slug
 * auto-derives from the name until the user edits it directly.
 */
export function CreateWorkspaceModal({ onClose, onCreated }: { onClose: () => void; onCreated?: () => void }) {
  const { createWorkspace } = useAuth();
  const { t } = useI18n();
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugEdited, setSlugEdited] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const shownSlug = slugEdited ? slug : slugify(name);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const n = name.trim();
      if (!n || busy) return;
      setBusy(true);
      setError(null);
      try {
        await createWorkspace(n, shownSlug || undefined);
        onCreated?.();
        onClose();
      } catch {
        setError(t("collab.workspace.createError"));
        setBusy(false);
      }
    },
    [name, shownSlug, busy, createWorkspace, onClose, onCreated, t]
  );

  return (
    <div className="ws-modal-overlay" onMouseDown={onClose}>
      <div className="ws-modal" role="dialog" aria-modal="true" onMouseDown={(e) => e.stopPropagation()}>
        <div className="ws-modal-icon" aria-hidden>🗂</div>
        <h2 className="ws-modal-title">{t("collab.workspace.createTitle")}</h2>
        <p className="ws-modal-desc">{t("collab.workspace.createDesc")}</p>

        <form onSubmit={submit}>
          <div className="ws-field">
            <label className="ws-field-label" htmlFor="ws-new-name">{t("collab.workspace.nameLabel")}</label>
            <input
              id="ws-new-name"
              className="ws-input"
              placeholder={t("collab.workspace.namePlaceholder")}
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus={shouldAutoFocusEditable()}
              maxLength={60}
            />
          </div>

          <div className="ws-field">
            <label className="ws-field-label" htmlFor="ws-new-slug">{t("collab.workspace.slugLabel")}</label>
            <div className="ws-slug">
              <span className="ws-slug-prefix" aria-hidden>/</span>
              <input
                id="ws-new-slug"
                className="ws-slug-input"
                placeholder={t("collab.workspace.slugPlaceholder")}
                value={shownSlug}
                onChange={(e) => {
                  setSlugEdited(true);
                  setSlug(slugify(e.target.value));
                }}
              />
            </div>
          </div>

          {error ? <p className="ws-warn">{error}</p> : null}

          <div className="ws-modal-actions">
            <button type="button" className="ws-btn" onClick={onClose}>
              {t("collab.workspace.cancel")}
            </button>
            <button type="submit" className="ws-btn primary" disabled={!name.trim() || busy}>
              {busy ? "…" : t("collab.workspace.createBtn")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
